import React, { useState, useEffect } from "react";
import { IActionHandler, OrgInfo, AdminDashboardUser, UserDataUsage, AdminUserBase, Sort } from "@preveil-api";
import {
  Account, getOppositeSortDirection, GlobalErrorMessages, MessageAnchors, MessageHandlerDisplayType, SORT_DIRECTION, useAppDispatch, sortBy, Message, AdminErrorMessages,
  account_types, DashboardUsage, BOM, dayjs, USER_STORAGE_HEADERS, isSameUser, useGetOrgInfo, UserResultsMode
} from "src/common";
import { collectionApi, useGetOrgDataUsageByUsersQuery } from "src/common/api/collection/collection-rtk-api";
import { InfoPopover } from "src/components";
import { Toolbar, ListView } from "./dashboard-users";
import { uiActions } from "src/store";
import saveAs from "file-saver";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo
  setPropsUsersList: (users_list: AdminDashboardUser[]) => void
}
function DashboardUsersComponent(props: AllProps) {
  const { current_account, org_info, setPropsUsersList } = props;
  const dispatch = useAppDispatch();
  const [filter_param, setFilterParam] = useState<string>("");
  const [users_list, setUsersList] = useState<AdminDashboardUser[]>([]);
  const [filtered_list, setFilteredList] = useState<AdminDashboardUser[]>([]);
  const [sort, setSort] = useState<Sort<keyof AdminDashboardUser>>({ field: "display_name", direction: SORT_DIRECTION.ascending });
  const [is_refreshing, setIsRefreshing] = useState<boolean>(false);
  // Hooks
  const { data: usage_data, error: usage_error, isLoading: usage_loading, isFetching: usage_fetching } = useGetOrgDataUsageByUsersQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: { entity_id: org_info.org_id },
  });
  const { data: org_data, error: org_data_error, refresh: refreshOrgInfo } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight);
  const is_loading = usage_loading || usage_fetching || is_refreshing || !org_data;

  // Description: Triggers the lightweight org-info fetch on mount.
  useEffect(() => {
    refreshOrgInfo();
  }, []);

  // Description: Clears the refresh spinner once org_data lands.
  useEffect(() => {
    setIsRefreshing(false);
  }, [org_data, org_data_error]);

  // Description: Initialize the data for the table, dispatch error if any
  useEffect(() => {
    (!!usage_data && !!org_data?.lightweight_org_users) &&
      handleInitializeUsers(usage_data.user_details, org_data.lightweight_org_users);

    (usage_error || !!org_data_error) &&
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_fetching_user_storage, MessageHandlerDisplayType.toastr)));
  }, [usage_data, org_data, usage_error, org_data_error]);

  const UserStorageTableRequests = {
    // Description: Set the sort for the table
    handleSetSort: (params: "display_name" | "private_usage" | "shared_storage" | "file_count") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortBy(filtered_list, { field: params, direction });
      setSort({ field: params, direction });
      setFilteredList(sorted_requests);
    },
    // Description: Search the data for the table
    handleSearchParam: (param?: string) => {
      if (!!param) {
        setFilterParam(param.toLowerCase());
        const filtered = users_list.filter((user: AdminDashboardUser) => {
          return Object.values(user).some(field =>
            _.isString(field) && field.toLowerCase().includes(param.toLowerCase())
          );
        });
        setFilteredList(filtered);
      } else {
        setFilterParam("");
        setFilteredList(users_list);
      }
    },
    // Description: Refetch the data for the table
    handleGetRefreshRequest: () => {
      setIsRefreshing(true);
      dispatch(collectionApi.util.invalidateTags(["org_data_usage"]));
      refreshOrgInfo();
    },
    // Description: Export the data for the table to a CSV file
    handleExportCSV: () => {
      try {
        const csv_string = [
          [...USER_STORAGE_HEADERS],
          ...users_list.map((user: AdminDashboardUser) => [
            user.display_name || "",
            user.user_id || "",
            user.account_type === account_types.full ? "Full Account User" : user.account_type === account_types.express ? "Express User" : "Pending",
            DashboardUsage.convertUsageToFriendlySizeList(user.private_usage),
            DashboardUsage.convertUsageToFriendlySizeList(user.shared_storage),
            user.file_count?.toString() || "0"
          ])
        ]
          .map(row => row.join(","))
          .join("\n");

        const csv_data = `${BOM}${csv_string}`;
        const blob = new Blob([csv_data], { type: "text/csv; charset=utf-8" });
        saveAs(blob, `user-storage-${dayjs(new Date()).format("MM.DD.YYYY-HH_mm_ss")}.csv`);
      } catch (error) {
        dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_export_user_storage_csv, MessageHandlerDisplayType.toastr), error));
      }
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((UserStorageTableRequests as any)[callback] instanceof Function) {
      (UserStorageTableRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      UserStorageTableRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // Description: Initialize the data for the table
  function handleInitializeUsers(user_details: UserDataUsage[], cs_users: AdminUserBase[]) {
    const _users_list = user_details.map((user_data_usage: UserDataUsage) => {
      const user = cs_users.find((user: AdminUserBase) => isSameUser(user.user_id, user_data_usage.user_id));
      return {
        ...user_data_usage,
        display_name: user?.display_name || "",
        account_type: user?.account_type || null,
        private_usage: user_data_usage.default_collections.active_bytes,
        shared_storage: user_data_usage.shared_collections.active_bytes,
        file_count: Number(user_data_usage.shared_collections.active_count) + Number(user_data_usage.default_collections.active_count)
      };
    });
    setUsersList(_users_list);
    setFilteredList(_users_list);
    setPropsUsersList(_users_list);
  }

  // Description: Render the dashboard users table component
  return (
    <>
      <div className="d-flex align-items-center">
        <h3>User Storage Details</h3>
        <InfoPopover
          className="ms-1 mb-2"
          message="The storage amounts include only final drive document versions and no deleted items. The information is updated daily. " />
      </div>
      <Toolbar is_loading={is_loading} filter_param={filter_param} users_list={users_list} handleAction={handlePageActions} />
      <ListView is_loading={is_loading} users_list={filtered_list} sort={sort} handleAction={handlePageActions} />
    </>
  );
}

export default React.memo(DashboardUsersComponent);
